// src/pages/Landing.jsx
// Public marketing landing page shown at "/" for logged-out visitors.
// Conversion journey: Discover → Understand → Sign up → Create or join.

import React from "react";
import { Link } from "react-router-dom";
import { Card as FcCard, LinkButton as FcLinkButton } from '../ui';

const GREEN = "#284239";
const ORANGE = "#BA6830";
const ORANGE_HOVER = "#a95c28";

function FormatCard({ emoji, title, children }) {
  return (
    <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl shadow-sm border border-ui-border p-6 text-left h-full">
      <div className="text-3xl mb-3" aria-hidden>{emoji}</div>
      <h3 className="text-lg font-bold mb-2" style={{ color: GREEN }}>{title}</h3>
      <p className="text-content-secondary text-sm leading-relaxed">{children}</p>
    </FcCard>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-white"
        style={{ backgroundColor: ORANGE }}
      >
        {n}
      </div>
      <div>
        <h3 className="font-bold mb-1" style={{ color: GREEN }}>{title}</h3>
        <p className="text-content-secondary text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-surface-canvas flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-ui-border" style={{ backgroundColor: GREEN }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="" className="w-8 h-8 rounded" />
            <span className="text-white text-lg font-extrabold tracking-tight">Footy Coach</span>
          </div>
          <nav aria-label="Navigation" className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              className="text-white/90 hover:text-white font-semibold text-sm px-3 py-2"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: ORANGE }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = ORANGE_HOVER)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = ORANGE)}
            >
              Sign up free
            </Link>
          </nav>
        </div>
      </header>

      <main id="fc-main-content" className="flex-1">
        {/* Hero */}
        <section className="px-4 py-16 sm:py-24 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-5" style={{ color: GREEN }}>
              Run football prediction competitions with your mates.
            </h1>
            <p className="text-lg text-content-secondary mb-8 max-w-2xl mx-auto">
              Footy Coach is the free toolkit for last-man-standing, season-long and World Cup
              prediction games, for your group chat, office, pub, club or community.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/signup"
                className="text-white font-bold text-base px-7 py-3.5 rounded-lg transition-colors shadow-sm"
                style={{ backgroundColor: ORANGE }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = ORANGE_HOVER)}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = ORANGE)}
              >
                Create your free competition
              </Link>
              <FcLinkButton variant="secondary"
                to="/login"
                className="border-2"
                style={{ color: GREEN, borderColor: GREEN }}
              >
                I already have an account
              </FcLinkButton>
            </div>
            <p className="text-sm text-content-secondary mt-5">
              You run the game, we run the tech. Footy Coach never handles entry fees or prize money.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="px-4 py-14 bg-surface-card border-y border-ui-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-10" style={{ color: GREEN }}>
              How it works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Step n={1} title="Create or join a competition">
                Start your own in seconds and share a join link, or hop into one a friend set up.
              </Step>
              <Step n={2} title="Pick your team each round">
                Choose a team for the gameweek. Depending on the format, teams you&apos;ve used may
                lock, so pick wisely.
              </Step>
              <Step n={3} title="Climb the leaderboard">
                Points, survival and bragging rights update automatically as real results come in.
              </Step>
            </div>
          </div>
        </section>

        {/* Formats */}
        <section className="px-4 py-14">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-3" style={{ color: GREEN }}>
              Pick your format
            </h2>
            <p className="text-center text-content-secondary mb-10 max-w-2xl mx-auto">
              Every competition can be public or private, and multiple people can help you run it.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormatCard emoji="🏆" title="Knockout (Last Man Standing)">
                Pick a winning team each round. Get it wrong and you&apos;re out. Last one standing
                takes the crown.
              </FormatCard>
              <FormatCard emoji="📊" title="Season Long">
                Rack up points across a full season. Consistency and clever picks win the table.
              </FormatCard>
              <FormatCard emoji="🌍" title="World Stage">
                Predict scores through a major international tournament and top the global standings.
              </FormatCard>
            </div>
          </div>
        </section>

        {/* Perfect for your group */}
        <section className="px-4 py-14 bg-surface-card border-y border-ui-border">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-4" style={{ color: GREEN }}>
              Made for your group
            </h2>
            <p className="text-content-secondary mb-8 max-w-2xl mx-auto">
              Run a private competition for exactly who you want. Invite by link and manage who&apos;s in.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {["Friends", "Workplaces", "Pubs", "Clubs", "Communities", "Group chats"].map((g) => (
                <span
                  key={g}
                  className="px-4 py-2 rounded-full text-sm font-semibold border"
                  style={{ color: GREEN, borderColor: "#e5e7eb", backgroundColor: "#F3F3F3" }}
                >
                  {g}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* No fees / you're the organiser */}
        <section className="px-4 py-14">
          <div className="max-w-3xl mx-auto">
            <FcCard as="div" padding="standard" className="bg-surface-card rounded-xl border border-ui-border shadow-sm p-8 text-center">
              <div className="text-3xl mb-3" aria-hidden>🤝</div>
              <h2 className="text-2xl font-extrabold mb-3" style={{ color: GREEN }}>
                You&apos;re the organiser, not us
              </h2>
              <p className="text-content-secondary leading-relaxed">
                Footy Coach gives you the tools to run the competition: fixtures, picks, scoring,
                leaderboards and entrant management. Any entry fee or prize is arranged entirely
                between you and your players. <span className="font-semibold">Footy Coach does not
                collect, hold or pay out any money.</span>
              </p>
            </FcCard>
          </div>
        </section>

        {/* Why sign up / final CTA */}
        <section className="px-4 py-16 text-center" style={{ backgroundColor: GREEN }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-extrabold text-white mb-4">Ready to kick off?</h2>
            <p className="text-white/80 mb-8">
              Create a free account to start a competition, join your friends&apos; games, and track
              every pick in one place.
            </p>
            <Link
              to="/signup"
              className="inline-block text-white font-bold text-base px-8 py-4 rounded-lg transition-colors shadow"
              style={{ backgroundColor: ORANGE }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = ORANGE_HOVER)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = ORANGE)}
            >
              Sign up free
            </Link>
            <div className="mt-4">
              <Link to="/login" className="text-white/80 hover:text-white text-sm font-semibold underline">
                or log in
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-surface-card border-t border-ui-border">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="" className="w-6 h-6 rounded" />
            <span className="font-bold text-sm" style={{ color: GREEN }}>Footy Coach</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-content-secondary">
            <Link to="/terms" className="hover:underline">Terms</Link>
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <span>© {new Date().getFullYear()} Footy Coach</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
